import { Browser } from "@capacitor/browser";
import { logNativeFlow } from "@/lib/native-flow-diagnostics";
import { isPaymentUserCancel } from "@/lib/payment-cancel";
import { PAYMENT_DEEPLINK_KEY } from "@/lib/checkout/payment-window-close";

/**
 * Capacitor 결제 브릿지 deep link 핸들러.
 *
 * 포맷: locarora://payment-complete?paymentId=...&orderGroupId=...
 *                                 &code=...&message=...&pgCode=...&pgMessage=...
 *
 * 결제 시작 시 useCheckoutPayment 가 `@capacitor/browser` 로 SFSafariViewController를
 * 열어 `/<locale>/checkout/external-pay` 에서 V2 SDK를 실행한다. 결제가 끝나면
 * PortOne SDK가 `/<locale>/checkout/payment-bridge-callback` 으로 redirect 하고,
 * 그 페이지가 즉시 위 deep link로 redirect → iOS가 Capacitor 앱 활성화 →
 * 우리 앱의 appUrlOpen 리스너(`CapacitorBootstrap`)가 이 함수를 호출.
 *
 * 처리:
 *   1. SFSafariViewController를 닫는다.
 *   2. 결제 결과(에러 / 사용자 취소 / 성공)에 따라 적절한 페이지로 navigate.
 *      - 성공: /<locale>/checkout/success?paymentId=...&orderGroupId=...
 *      - 실패/취소: /<locale>/checkout?error=... (사용자가 다시 시도)
 *
 * 반환: 이 deep link가 우리 책임이면 true. 그 외 false → caller가 다른 dispatcher
 * 로 fallthrough 할 수 있도록.
 */
/** `/<locale>/checkout` (결제 화면 자체, 하위 경로 제외). */
export function isCheckoutPagePath(pathname: string): boolean {
  return /^\/[^/]+\/checkout\/?$/.test(pathname);
}

export async function processPaymentDeepLink(
  urlStr: string,
): Promise<boolean> {
  let url: URL;
  try {
    url = new URL(urlStr);
  } catch {
    return false;
  }

  if (url.protocol !== "locarora:" || url.host !== "payment-complete") {
    return false;
  }

  const params = url.searchParams;
  const paymentId = params.get("paymentId") ?? "";
  const orderGroupId = params.get("orderGroupId") ?? "";
  const code = params.get("code");
  const message = params.get("message");
  const pgCode = params.get("pgCode");
  const pgMessage = params.get("pgMessage");

  console.info("[payment] deep link received", {
    paymentId,
    orderGroupId,
    code,
    message,
    pgCode,
    pgMessage,
  });
  logNativeFlow("payment-deeplink", "received", {
    paymentId,
    orderGroupId,
    code,
    message,
    pgCode,
    pgMessage,
  }, code ? "warn" : "info");

  // 창 닫힘 처리(payment-window-close)가 같은 결제를 다시 처리하지 않게 — 창을 닫기 전에 표시.
  try {
    sessionStorage.setItem(PAYMENT_DEEPLINK_KEY, paymentId);
  } catch {
    // 무시
  }

  // SFSafariViewController 닫기. iOS 에선 안 닫혀도 deep link로 앱이 포어그라운드로
  // 올라온 상태라 큰 문제는 없지만, 닫아주면 사용자가 추가 액션 없이 결제 결과
  // 화면을 본다.
  try {
    await Browser.close();
    logNativeFlow("payment-deeplink", "browser.close.success", { paymentId });
  } catch {
    logNativeFlow("payment-deeplink", "browser.close.failed", { paymentId }, "warn");
    // 무시 — 이미 닫혔거나 Android에서 close 실패해도 UX 영향 없음
  }

  // 현재 path 에서 locale 추출 — `/ko/...` 형태
  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  const locale = pathSegments[0] || "en";

  if (code) {
    // 사용자 취소 / PG 거부 / SDK 에러 등. 사용자 취소는 silent로 checkout 으로
    // 복귀, 그 외엔 error 메시지 노출.
    if (isPaymentUserCancel({ code, message, pgCode, pgMessage })) {
      logNativeFlow("payment-deeplink", "navigate.cancel", { paymentId });
      // 사용자 취소: 결제 페이지로 조용히 복귀 — 실패 다이얼로그 없음(10-12 확정).
      // 결제 창은 결제 화면 위에 떠 있었으니 그 화면에 그대로 남는다 — /checkout 로 새로
      // 가면 장바구니 결제의 ?source=cart&items=… 가 사라져 결제 정보를 잃는다(10-30).
      if (!isCheckoutPagePath(window.location.pathname)) {
        window.location.href = `/${locale}/checkout`;
      }
    } else {
      const errorParam = encodeURIComponent(message || code);
      logNativeFlow("payment-deeplink", "navigate.error", {
        paymentId,
        code,
        message,
      }, "warn");
      window.location.href = `/${locale}/checkout?paymentError=${errorParam}`;
    }
    return true;
  }

  if (!paymentId) {
    console.warn(
      "[payment] deep link missing paymentId — falling back to /checkout",
    );
    logNativeFlow("payment-deeplink", "missing-payment-id", {
      code,
      message,
    }, "warn");
    window.location.href = `/${locale}/checkout`;
    return true;
  }

  // 결제 완료 플래그 저장 — 기존 success 페이지가 이 키로 뒤로가기 방지 동작 함.
  try {
    sessionStorage.setItem("locarora_payment_completed", paymentId);
  } catch {
    // sessionStorage 접근 실패는 무시
  }

  const successParams = new URLSearchParams({ paymentId });
  if (orderGroupId) successParams.set("orderGroupId", orderGroupId);
  logNativeFlow("payment-deeplink", "navigate.success", {
    paymentId,
    orderGroupId,
  });
  window.location.href = `/${locale}/checkout/success?${successParams.toString()}`;
  return true;
}
